import { supabase } from './supabase';

export interface ChatConversation {
  id: string;
  user_id: string;
  session_id: string;
  started_at: string;
  ended_at?: string;
  total_messages: number;
  ai_responses: number;
  user_messages: number;
  created_at: string;
  updated_at: string;
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  user_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  message_order: number;
  is_fallback: boolean;
  model_used?: string;
  response_time_ms?: number;
  tokens_used?: number;
  created_at: string;
}

export interface ConversationHistory {
  conversation_id: string;
  session_id: string;
  started_at: string;
  ended_at?: string;
  total_messages: number;
  first_message: string;
  last_message: string;
}

export const chatConversationService = {
  // Start a new conversation
  async startConversation(userId: string, sessionId: string): Promise<string> {
    const { data, error } = await supabase
      .from('chat_conversations')
      .insert({
        user_id: userId,
        session_id: sessionId
      })
      .select('id')
      .single();

    if (error) {
      console.error('Error starting conversation:', error);
      throw new Error('Failed to start conversation');
    }

    return data.id;
  },

  // Add a message to a conversation
  async addMessage(
    conversationId: string,
    userId: string,
    role: 'user' | 'assistant' | 'system',
    content: string,
    messageOrder: number,
    isFallback: boolean = false,
    modelUsed?: string,
    responseTimeMs?: number,
    tokensUsed?: number
  ): Promise<string> {
    const { data, error } = await supabase
      .from('chat_messages')
      .insert({
        conversation_id: conversationId,
        user_id: userId,
        role,
        content,
        message_order: messageOrder,
        is_fallback: isFallback,
        model_used: modelUsed,
        response_time_ms: responseTimeMs,
        tokens_used: tokensUsed
      })
      .select('id')
      .single();

    if (error) {
      console.error('Error adding message:', error);
      throw new Error('Failed to add message');
    }

    return data.id;
  },

  // End a conversation
  async endConversation(conversationId: string): Promise<void> {
    const { error } = await supabase
      .rpc('end_conversation', { conversation_uuid: conversationId });

    if (error) {
      console.error('Error ending conversation:', error);
      throw new Error('Failed to end conversation');
    }
  },

  // Get conversation history for a user
  async getUserConversations(userId: string, limit: number = 10): Promise<ConversationHistory[]> {
    const { data, error } = await supabase
      .rpc('get_user_conversations', {
        user_uuid: userId,
        limit_count: limit
      });

    if (error) {
      console.error('Error getting user conversations:', error);
      throw new Error('Failed to get conversation history');
    }

    return data || [];
  },

  // Get messages for a specific conversation
  async getConversationMessages(conversationId: string): Promise<ChatMessage[]> {
    const { data, error } = await supabase
      .rpc('get_conversation_messages', { conversation_uuid: conversationId });

    if (error) {
      console.error('Error getting conversation messages:', error);
      throw new Error('Failed to get conversation messages');
    }

    return data || [];
  },

  // Get conversation statistics
  async getConversationStats(userId: string): Promise<{
    totalConversations: number;
    totalMessages: number;
    avgMessagesPerConversation: number;
    avgResponseTime: number;
  }> {
    const { data, error } = await supabase
      .from('chat_conversations')
      .select('total_messages, ai_responses, user_messages')
      .eq('user_id', userId);

    if (error) {
      console.error('Error getting conversation stats:', error);
      throw new Error('Failed to get conversation statistics');
    }

    const conversations = data || [];
    const totalConversations = conversations.length;
    const totalMessages = conversations.reduce((sum, conv) => sum + conv.total_messages, 0);
    const avgMessagesPerConversation = totalConversations > 0 ? totalMessages / totalConversations : 0;

    // Get average response time from messages
    const { data: messagesData, error: messagesError } = await supabase
      .from('chat_messages')
      .select('response_time_ms')
      .eq('user_id', userId)
      .not('response_time_ms', 'is', null);

    if (messagesError) {
      console.error('Error getting response times:', messagesError);
    }

    const responseTimes = messagesData || [];
    const avgResponseTime = responseTimes.length > 0 
      ? responseTimes.reduce((sum, msg) => sum + (msg.response_time_ms || 0), 0) / responseTimes.length 
      : 0;

    return {
      totalConversations,
      totalMessages,
      avgMessagesPerConversation: Math.round(avgMessagesPerConversation * 100) / 100,
      avgResponseTime: Math.round(avgResponseTime)
    };
  },

  // Generate a unique session ID
  generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}; 